import { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import { toast } from "react-toastify";
import { clearCart } from "../redux/cartSlice";
import { HiCreditCard } from "react-icons/hi";
import styles from "../styles/checkout.module.css";
import { mapToArabic } from "../utils/mapToArabic";
import { MdAttachMoney, MdClose } from "react-icons/md";
import PanelSkeleton from "../components/PanelSkeleton";
import ProductImage from "../components/ProductImage";
import axios from "axios";
import { BsPhone, BsGeoAlt, BsTruck } from 'react-icons/bs';
import { ImSpinner2 } from "react-icons/im";
import { FiCheck, FiFileText, FiShoppingBag, FiTag } from "react-icons/fi";

const Checkout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [pay, setPay] = useState("cash on delivery");
  const [code, setCode] = useState({promocode: "", discount: "", validity: ""});
  const [deposit, setDeposit] = useState("");
  const [loading, setLoading] = useState(false);
  const [activating, setActivating] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const user = useSelector((state) => state.auth.user);
  const cartItems = useSelector((state) => state.cart.items);
  const directBuy = location.state?.directBuy || null;
  const orderItems = directBuy ? [directBuy] : cartItems;
  const [zones, setZones] = useState([]);
  const [selectedZone, setSelectedZone] = useState({value: "", label: ""});
  const inputRef = useRef(null);

  useEffect(() => {
    if (!location.state?.fromCart) {
      setLoading(true);
      navigate("/cart", { replace: true, state: {} });
    }
  }, [location.state?.fromCart, navigate]);

  useEffect(() => {
    if (!user || !cartItems) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [user, cartItems]);

  const fetchDelivery = (async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/orders/delivery`);
      setZones(res.data.data);
    } catch {
      toast.error(<span className="toast">حدث خطأ اثناء تحميل بيانات التوصيل. حاول مرة اخرى</span>)
    }
  });

  useEffect(() => {
    fetchDelivery();
  }, []);

  const needsBigVan = orderItems.some(item => item.dimension?.van_type === "big");
  const options = zones.map(zone => ({
    value: needsBigVan ? zone.bigVanFees : zone.smallVanFees,
    label: zone.area,
  }));

  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      color: state.isSelected ? '#e1ae8b' : '#9B773D',
      fontWeight: state.isSelected ? 'bold' : 'normal'
    })
  };

  let total = 0;

  for (const item of orderItems) {
    const itemTotal = (item.price || 0) * (item.quantity || 1);
    total += itemTotal
  }

  const discount = ((code.discount * total) / 100).toFixed(2);

  const net = (total - (discount || 0) + Number(selectedZone.value || 0)).toFixed(2);

  const orderData = {
    shipping_details: {
      name: user.name,
      mobile: user.mobile,
      email: user.email,
      district: user.district,
      street: user.street,
      note: user.note
    },
    delivery_area: selectedZone.label,
    delivery_fees: selectedZone.value,
    promocode: code.validity === 1 ? code.promocode : null,
    payment: pay,
    deposit: Number(deposit) || 0,
    products: orderItems.map((item) => ({
      product_id: item.id,
      pd_id: item.dimension.id,
      dim_id: item.dimension.dim_id,
      product_name: item.name,
      width: item.dimension.width,
      height: item.dimension.height,
      material: item.material,
      price: item.price,
      image_url: item.image_url,
      quantity: item.quantity
    }))
  };

  const handleActivation = async () => {
    setActivating(true)

    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/orders/promocode/check`, { code: code.promocode }, { withCredentials: true });
      setCode(response.data.data);
    } catch (error) {
      console.error("Promocode activation error:", error);
    } finally {
      setActivating(false);
    }
  }

  const handleConfirmOrder = async () => {
    if (!selectedZone.value) {
      return toast.error("اختر منطقة التوصيل");
    }

    if (!pay) {
      return toast.error("اختر طريقة الدفع");
    }

    setConfirming(true);

    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/orders/add`, orderData, { withCredentials: true });
  
      toast.success(<span className="toast">تم عمل الطلب بنجاح</span>, {
        onClose: () => {
          if (!directBuy) dispatch(clearCart());
          navigate("/thankyou");
        },
        autoClose: 1000
      });
    } catch {
      toast.error(<span className="toast">لم نتمكن من عمل الطلب. حاول مرة اخرى</span>);
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.hero}>
        <h4>تأكيد الطلب</h4>
        <p>راجع بياناتك واختر طريقة الدفع لإتمام طلبك</p>
      </div>

      {loading ? (
        <PanelSkeleton count={2} />
      ) : (
        <div className={styles.layout}>
          <div className={styles.mainCol}>
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <span className={styles.cardIcon}><BsGeoAlt /></span>
                <h5>عنوان التوصيل</h5>
              </div>

              <div className={styles.addressGrid}>
                <div className={styles.addressItem}>
                  <BsPhone className={styles.addressIcon} />
                  <div>
                    <span className={styles.addressLabel}>رقم الهاتف</span>
                    <p className="mobile">{user.mobile}</p>
                  </div>
                </div>

                <div className={styles.addressItem}>
                  <BsGeoAlt className={styles.addressIcon} />
                  <div>
                    <span className={styles.addressLabel}>العنوان</span>
                    <p>{user.street}{user.district && <>, {user.district}</>}</p>
                  </div>
                </div>

                {user.note &&
                  <div className={styles.addressItem}>
                    <FiFileText className={styles.addressIcon} />
                    <div>
                      <span className={styles.addressLabel}>ملاحظة</span>
                      <p>{user.note}</p>
                    </div>
                  </div>
                }
              </div>
            </div>

            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <span className={styles.cardIcon}><FiShoppingBag /></span>
                <h5>محتويات الطلب ({orderItems.length})</h5>
              </div>

              <div className={styles.itemsList}>
                {orderItems.map((item) => (
                  <div key={item} className={styles.orderItem} onClick={() => navigate(`/product/${item.id}`)}>
                    <div className={styles.itemImg}>
                      <ProductImage
                        url={item.image_url}
                        alt={item.name}
                        loading="lazy"
                      />
                    </div>

                    <div className={styles.itemDetails}>
                      <p className={styles.itemName}>{item.name}</p>
                      <div className={styles.itemTags}>
                        <span className="brownlabel">{item.dimension.width} × {item.dimension.height}</span>
                        <span className="brownlabel">
                          {mapToArabic(item.material).text}
                        </span>
                      </div>
                    </div>

                    <div className={styles.itemPrice}>
                      <span className={styles.itemQty}>{item.quantity} × {item.price}</span>
                      <strong className={styles.itemTotal}>{(item.price * item.quantity).toFixed(2)} ₪</strong>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className={styles.summaryCol}>
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <span className={styles.cardIcon}><BsTruck /></span>
                <h5>التوصيل</h5>
              </div>

              <div>
                <Select
                  options={options}
                  placeholder="اختر المنطقة"
                  onChange={(newValue) => {
                    setSelectedZone(newValue)
                  }}
                  styles={customStyles}
                  className={styles.selectWrap}
                  classNamePrefix="react-select"
                  isSearchable={false}
                />

                <span className={`${styles.vanBadge} ${needsBigVan ? styles.big : styles.small}`}>
                  {needsBigVan ? "سيارة كبيرة" : "سيارة صغيرة"}
                </span>
              </div>

              <div className={`${styles.promoWrap} ${code.validity === 1 ? styles.valid : ''} ${(code.validity === 0 || code.validity === 2) ? styles.invalid : ''}`}>
                <FiTag className={styles.promoIcon} />
                <input
                  placeholder="كود الخصم"
                  value={code.promocode}
                  onChange={(e) => setCode({promocode: e.target.value.trim().replace(/\s+/g, ''), discount: "", validity: ""})}
                  ref={inputRef}
                />

                {(code.promocode && code.validity === "") ?
                  <button
                    className={styles.promoAction}
                    onClick={handleActivation}
                    disabled={activating}
                  >{activating ? <ImSpinner2 className="smallspinner" style={{fill: "#0f79fa"}} /> :
                    "تفعيل"
                  }</button> :
                  (code.promocode && code.validity === 1) ?
                  <FiCheck className="checkmark" style={{color: "#2fa92f", flexShrink: 0}} /> :
                  (code.promocode && (code.validity === 0 || code.validity === 2)) ?
                  <MdClose
                    className="delete cursor"
                    style={{flexShrink: 0}}
                    onClick={() => {setCode({promocode: "", discount: "", validity: ""});
                      inputRef.current.focus();
                    }}
                  /> :
                  null
                }
              </div>

              {code.validity === 1 && <p className={styles.promoComment} style={{color: "#2fa92f"}}>مبروك حصلت على خصم</p>}
              {code.validity === 0 && <p className={styles.promoComment} style={{color: "red"}}>الكود غير صحيح</p>}
              {code.validity === 2 && <p className={styles.promoComment} style={{color: "red"}}>هذا الكود تم استخدامه من قبل</p>}

              <div className={styles.breakdown}>
                <div className={styles.summaryRow}>
                  <span>إجمالى</span>
                  <span>{total.toFixed(2)} ₪</span>
                </div>

                <div className={styles.summaryRow}>
                  <span>رسوم التوصيل</span>
                  <span>{selectedZone.value || 0} ₪</span>
                </div>

                {code.validity === 1 &&
                  <div className={`${styles.summaryRow} ${styles.discount}`}>
                    <span>الخصم</span>
                    <span>- {discount || 0} ₪</span>
                  </div>
                }

                <div className={styles.netRow}>
                  <span>الصافى</span>
                  <span>{net} ₪</span>
                </div>
              </div>

              <div className={styles.depositBlock}>
                <label className={styles.depositLabel}>دفعة مقدمة / عربون (₪)</label>
                <input
                  className={styles.depositInput}
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0"
                  value={deposit}
                  onChange={(e) => setDeposit(e.target.value)}
                />
                {Number(deposit) > 0 && (
                  <p className={styles.remainingNote}>
                    المبلغ المتبقي عند الاستلام:{" "}
                    <strong>{(Number(net) - Number(deposit)).toFixed(2)} ₪</strong>
                  </p>
                )}
              </div>

              <div>
                <label className={styles.depositLabel}>طريقة الدفع</label>
                <div className={styles.paymentGrid} style={{marginTop: "1.5vw"}}>
                  <button
                    className={`${styles.paymentTile} ${styles.cash} ${pay === "cash on delivery" ? styles.selected : ''}`}
                    onClick={() => setPay("cash on delivery")}
                  >
                    <span className={styles.paymentIconWrap}>
                      <MdAttachMoney className={styles.paymentIcon} />
                    </span>
                    <span className={styles.paymentLabel}>نقداً عند الاستلام</span>
                    <span className={`${styles.radioDot} ${pay === "cash on delivery" ? styles.active : ''}`} />
                  </button>

                  <button
                    className={`${styles.paymentTile} ${styles.card} ${styles.disabled}`}
                    disabled
                  >
                    <span className={styles.paymentIconWrap}>
                      <HiCreditCard className={styles.paymentIcon} />
                    </span>
                    <span className={styles.paymentLabel}>بطاقة ائتمان</span>
                    <span className={styles.comingSoon}>قريباً</span>
                  </button>
                </div>
              </div>

              <button
                onClick={handleConfirmOrder}
                disabled={confirming}
                className={`buttonNoBorder ${styles.confirmBtn}`}
              >
                {confirming ?
                  <ImSpinner2 className="smallspinner" /> :
                  <FiCheck className="checkmark" />
                } إتمام الطلب
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Checkout;
